import React, { useState, useEffect } from 'react';
import { FileText, Eye, X, ZoomIn, ZoomOut } from 'lucide-react';

interface DocumentPreviewProps {
  file: File;
  label: string;
}

export const DocumentPreview: React.FC<DocumentPreviewProps> = ({ file, label }) => {
  const [thumbnail, setThumbnail] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    // Generate thumbnail for image files
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setThumbnail(result);
        setPreviewUrl(result);
      };
      reader.readAsDataURL(file);
    } else if (file.type === 'application/pdf') {
      // For PDFs, we'll show a PDF icon
      setThumbnail('');
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, [file]);

  const handlePreview = () => {
    setShowPreview(true);
    setZoom(1);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setZoom(1);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  return (
    <>
      <div className="document-preview-item" onClick={handlePreview}>
        <div className="preview-thumbnail">
          {thumbnail ? (
            <img src={thumbnail} alt={label} />
          ) : (
            <div className="pdf-icon-wrapper">
              <FileText size={32} />
            </div>
          )}
          <div className="preview-overlay">
            <Eye size={20} />
            <span>Preview</span>
          </div>
        </div>
        <div className="preview-info">
          <span className="preview-label">{label}</span>
          <span className="preview-size">{(file.size / 1024).toFixed(1)} KB</span>
        </div>
      </div>

      {/* Lightbox Preview Modal */}
      {showPreview && (
        <div className="document-preview-lightbox" onClick={handleClosePreview}>
          <div className="preview-lightbox-overlay"></div>
          <div className="preview-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <div className="preview-lightbox-header">
              <h4>{label}</h4>
              <div className="preview-lightbox-controls">
                {thumbnail && (
                  <>
                    <button onClick={handleZoomOut} className="preview-control-btn" title="Zoom Out">
                      <ZoomOut size={20} />
                    </button>
                    <span className="preview-zoom-level">{Math.round(zoom * 100)}%</span>
                    <button onClick={handleZoomIn} className="preview-control-btn" title="Zoom In">
                      <ZoomIn size={20} />
                    </button>
                  </>
                )}
                <button onClick={handleClosePreview} className="preview-control-btn preview-close-btn" title="Close">
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="preview-lightbox-body">
              {file.type.startsWith('image/') && previewUrl ? (
                <img
                  src={previewUrl}
                  alt={label}
                  style={{ transform: `scale(${zoom})`, transition: 'transform 0.2s ease' }}
                />
              ) : file.type === 'application/pdf' && previewUrl ? (
                <iframe src={previewUrl} title={label} />
              ) : (
                <div className="preview-no-preview">
                  <FileText size={48} />
                  <p>Preview not available for this file type</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .document-preview-item {
          position: relative;
          cursor: pointer;
          background: var(--color-background-paper);
          border: 1px solid var(--color-border);
          border-radius: 8px;
          overflow: hidden;
          transition: all 0.2s ease;
        }

        .document-preview-item:hover {
          border-color: var(--color-primary-600);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .preview-thumbnail {
          position: relative;
          width: 100%;
          height: 150px;
          background: var(--color-background-tertiary);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .preview-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .pdf-icon-wrapper {
          color: var(--color-text-tertiary);
        }

        .preview-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          opacity: 0;
          transition: opacity 0.2s ease;
          color: white;
        }

        .document-preview-item:hover .preview-overlay {
          opacity: 1;
        }

        .preview-info {
          padding: 0.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .preview-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--color-text-primary);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .preview-size {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
        }

        /* Lightbox Styles */
        .document-preview-lightbox {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }

        .preview-lightbox-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
        }

        .preview-lightbox-content {
          position: relative;
          background: var(--color-background-paper);
          border-radius: 12px;
          max-width: 90vw;
          max-height: 90vh;
          width: 100%;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .preview-lightbox-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid var(--color-border);
        }

        .preview-lightbox-header h4 {
          margin: 0;
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .preview-lightbox-controls {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .preview-control-btn {
          background: transparent;
          border: 1px solid var(--color-border);
          border-radius: 6px;
          padding: 0.5rem;
          cursor: pointer;
          color: var(--color-text-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .preview-control-btn:hover {
          background: var(--color-background-tertiary);
          border-color: var(--color-primary-600);
        }

        .preview-control-btn.preview-close-btn {
          border-color: transparent;
          background: var(--color-error);
          color: white;
        }

        .preview-control-btn.preview-close-btn:hover {
          background: var(--color-error-dark);
        }

        .preview-zoom-level {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
          min-width: 50px;
          text-align: center;
        }

        .preview-lightbox-body {
          flex: 1;
          overflow: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          background: var(--color-background-tertiary);
        }

        .preview-lightbox-body img {
          max-width: 100%;
          max-height: 70vh;
          object-fit: contain;
          border-radius: 4px;
        }

        .preview-lightbox-body iframe {
          width: 100%;
          height: 70vh;
          border: none;
          border-radius: 4px;
        }

        .preview-no-preview {
          text-align: center;
          color: var(--color-text-secondary);
          padding: 2rem;
        }

        .preview-no-preview p {
          margin-top: 1rem;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .preview-thumbnail {
            height: 120px;
          }

          .preview-info {
            padding: 0.5rem;
          }

          .preview-label {
            font-size: 0.8125rem;
          }

          .preview-lightbox-content {
            max-width: 95vw;
            max-height: 95vh;
          }

          .preview-lightbox-header {
            padding: 0.875rem 1rem;
          }

          .preview-lightbox-header h4 {
            font-size: 1rem;
          }

          .preview-control-btn {
            padding: 0.375rem;
          }

          .preview-zoom-level {
            display: none;
          }

          .preview-lightbox-body img {
            max-height: 60vh;
          }

          .preview-lightbox-body iframe {
            height: 60vh;
          }
        }

        /* Small Mobile */
        @media (max-width: 480px) {
          .preview-thumbnail {
            height: 100px;
          }

          .preview-overlay span {
            font-size: 0.75rem;
          }
        }
      `}</style>
    </>
  );
};
